import React, { useContext, useLayoutEffect, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Button from '../../../components/bootstrap/Button';
import { HeaderRight } from '../../../layout/Header/Header';
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
	OffCanvasTitle,
} from '../../../components/bootstrap/OffCanvas';
import Alert from '../../../components/bootstrap/Alert';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import Icon from '../../../components/icon/Icon';
import ThemeContext from '../../../contexts/themeContext';
import LANG, { getLangWithKey } from '../../../lang';
import showNotification from '../../../components/extras/showNotification';
import useDarkMode from '../../../hooks/useDarkMode';
import Popovers from '../../../components/bootstrap/Popovers';
import { userAuth } from '../../../contexts/userContext';
import { CartContext } from '../../../contexts/CartContext';
import { useLanguageSwitch } from '../../../contexts/LanguageSwitchContext';
import { referralAgentApi } from '../../../api/referralAgentApi';
import './headerstlyle.css';
import {
	changeGoogleTranslateLanguage,
	getStoredLanguage,
} from '../../../helpers/languageSwitch';

// eslint-disable-next-line react/prop-types
const CommonHeaderRight = ({ beforeChildren }) => {
	const { t } = useTranslation();
	const { isLangSwitchEnabled } = useLanguageSwitch();
	const { setToken, token } = useContext(userAuth);
	const {
		notiCount,
		notiList,
		setNotiList,
		setNotiCount,
	} = useContext(CartContext);

	const { darkModeStatus, setDarkModeStatus } = useDarkMode();

	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const styledBtn = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const [pendingParams, setPendingParams] = useState({
		appid: '',
	});
	const [pageId, setPageId] = useState(1);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [hasMoreNotifications, setHasMoreNotifications] = useState(true);

	useEffect(() => {
		const storedTheme = localStorage.getItem('theme');
		if (storedTheme !== null) {
			setDarkModeStatus(storedTheme === 'dark');
		}
	}, [setDarkModeStatus]);

	useEffect(() => {
		localStorage.setItem('theme', darkModeStatus ? 'dark' : 'light');
	}, [darkModeStatus]);

	const [offcanvasStatus, setOffcanvasStatus] = useState(false);
	const [selectedLanguage, setSelectedLanguage] = useState(getStoredLanguage());

	const [windowWidth, setWindowWidth] = useState(window.innerWidth);

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const MOBILE_BREAKPOINT = Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE) || 576;

	const isMobile = windowWidth < MOBILE_BREAKPOINT;

	const mobilev = windowWidth < 400 ? 'ab' : 'cd';

	useEffect(() => {
		const nextLanguage = getStoredLanguage();
		setSelectedLanguage(nextLanguage);
	}, []);

	const changeLanguage = async (lang) => {
		const nextLanguage = await changeGoogleTranslateLanguage(lang);
		setSelectedLanguage(nextLanguage);

		showNotification(
			<span className='d-flex align-items-center'>
				<Icon icon={getLangWithKey(nextLanguage)?.icon} size='lg' className='me-1' />
				<span>{`Language changed to ${getLangWithKey(nextLanguage)?.text}`}</span>
			</span>,
		);
	};

	const fetchNotification = async (page = 1, append = false) => {
		try {
			const flechdata = await referralAgentApi(
				'get_agent_notification_list',
				{ limit: 25, page_id: page },
				token,
				setToken,
				navigate,
			);
			if (flechdata.type === 'success') {
				if (append) {
					setNotiList(prevList => [...prevList, ...flechdata.data]);
				} else {
					setNotiList(flechdata.data);
				}
				setNotiCount(flechdata.TotalRecords);
				setHasMoreNotifications(flechdata.data.length === 25);
			}
		} catch (error) {
			console.error('Error fetching notifications:', error);
		}
	};

	const loadMoreNotifications = async () => {
		if (isLoadingMore || !hasMoreNotifications) return;
		
		setIsLoadingMore(true);
		const nextPage = pageId + 1;
		setPageId(nextPage);
		await fetchNotification(nextPage, true);
		setIsLoadingMore(false);
	};

	const handleScroll = (e) => {
		const { scrollTop, scrollHeight, clientHeight } = e.target;
		if (scrollHeight - scrollTop === clientHeight) {
			loadMoreNotifications();
		}
	};

	const delAllNotification = async () => {
		try {
			const flechdata = await referralAgentApi(
				'update_agent_notification_status',
				{ notification_id: 'all' },
				token,
				setToken,
				navigate,
			);

			if (flechdata.type === 'Success' || flechdata.type === 'success') {
				setNotiList([]);
				setNotiCount(0);
			}
		} catch (error) {
			console.error('Error clearing all notifications:', error);
		}
	};

	const delNotification = async (id) => {
		try {
			const flechdata = await referralAgentApi(
				'update_agent_notification_status',
				{ notification_id: id },
				token,
				setToken,
				navigate,
			);

			if (flechdata.type === 'Success' || flechdata.type === 'success') {
				setNotiList(prevList => prevList.filter(item => item.id !== id));
				setNotiCount(prevCount => Math.max(0, prevCount - 1));
			}
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};

	useLayoutEffect(() => {
		document.documentElement.setAttribute('lang', selectedLanguage);
	});

	const navigate = useNavigate();

	const logouthandle = async () => {
		try {
			const flechdata = await referralAgentApi(
				'referral_agent_logout',
				{},
				token,
				setToken,
				navigate,
			);
			if (flechdata.type === 'success') {
				setToken({});
				localStorage.removeItem('user-auth');
				navigate('/login');
			} else {
				console.error('Error fetching services:', flechdata.message);
			}
		} catch (error) {
			console.error('Error fetching services:', error);
		}
	};

	return (
		<>
			<HeaderRight>
				<div className='row g-3'>
					{beforeChildren}
					<div className='col-auto'>
						{/* <Popovers trigger='hover' desc='Dark / Light mode'> */}
						<Button
							{...styledBtn}
							icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
							onClick={() => setDarkModeStatus((prev) => !prev)}
							data-tour='dark-mode'
						/>
						{/* </Popovers> */}
					</div>
					{isLangSwitchEnabled && (
						// <Popovers trigger='hover' desc='Change Language'>
						<div className='col-auto'>
							<Dropdown>
								<DropdownToggle hasIcon={false}>
									<Button
										{...styledBtn}
										icon={getLangWithKey(selectedLanguage)?.icon}
										data-tour='lang-selector'
									/>
								</DropdownToggle>
								<DropdownMenu isAlignmentEnd data-tour='lang-selector-menu'>
									{Object.keys(LANG).map((key) => (
										<DropdownItem key={LANG[key].lng}>
											<Button
												icon={LANG[key].icon}
												onClick={() => changeLanguage(LANG[key].lng)}>
												{LANG[key].text}
											</Button>
										</DropdownItem>
									))}
								</DropdownMenu>
							</Dropdown>
						</div>
						// </Popovers>
					)}

					{!isMobile && (
						<div className='col-auto'>
							<div className='col-auto position-relative'>
								<Button
									{...styledBtn}
									icon='Notifications'
									onClick={() => {
										setOffcanvasStatus(true);
										setPageId(1);
										setHasMoreNotifications(true);
										fetchNotification(1, false);
									}}
									aria-label='Notifications'
								/>
								{/* {(notiCount || notiCount !== 0) && (
							<span className='position-absolute top-0 start-90 translate-middle badge rounded-pill bg-danger'>
								{notiCount < 99 ? notiCount : '99+'}
							</span>
						)} */}
								{notiCount > 0 && (
									<span className='position-absolute top-0 start-90 translate-middle badge rounded-pill bg-danger'>
										{notiCount < 99 ? notiCount : '99+'}
									</span>
								)}
							</div>
						</div>
					)}

					{!isMobile && (
						<>
							<div className='col-auto'>
								<Dropdown>
									<DropdownToggle hasIcon={false}>
										{/* eslint-disable-next-line react/jsx-props-no-spreading */}
										<Button
											{...styledBtn}
											icon='Person'
											aria-label='Quick menu'
										/>
									</DropdownToggle>
									<DropdownMenu
										isAlignmentEnd
										size={windowWidth < 740 ? 'md' : 'lg'}
										className='py-0 overflow-hidden'>
										<div className='row g-0'>
											<div
												className={classNames(
													'col-12',
													'p-4',
													'd-flex justify-content-center',
													'fw-medium fs-5',
													'text-info',
													'border-bottom border-info',
													'bg-lo25-info',
												)}>
												<div
													style={{
														display: 'flex',
														flexDirection: 'column',
														alignItems: 'center',
													}}>
													<span
														style={{
															color: 'white',
															fontSize: '16px',
														}}>
														{token.name}
													</span>
													<span
														style={{
															color: 'white',
															fontSize: '12px',
														}}>
														{token.email}
													</span>
												</div>
											</div>
											<div
												className={classNames(
													'col-12 p-4 transition-base cursor-pointer',
													// 'border-end border-bottom',
													{ 'border-dark': darkModeStatus },
												)}>
												<div
													className='d-flex flex-column align-items-center justify-content-center'
													onClick={logouthandle}>
													{/* <Icon icon='Logout' size='3x' color='info' onClick={() => logout()}/> */}
													<div
														style={{
															display: 'flex',
															alignItems: 'center',
															gap: '10px',
															color: '#e17f7f',
														}}>
														<Icon icon='Logout' size='3x' />
														<span
															style={{
																fontWeight: '500',
																fontSize: '14px',
															}}>
															{t('Log Out')}
														</span>
													</div>
												</div>
											</div>
										</div>
									</DropdownMenu>
								</Dropdown>
							</div>
						</>
					)}
					<div className='col-auto'>
						{/* <Popovers trigger='hover' desc={t('Fullscreen')}> */}
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							icon={fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'}
							onClick={() => setFullScreenStatus(!fullScreenStatus)}
							aria-label='Toggle dark mode'
						/>
						{/* </Popovers> */}
					</div>
				</div>

				{!isMobile && (
					<OffCanvas
						id='notificationCanvas'
						titleId='offcanvasExampleLabel'
						placement='end'
						isOpen={offcanvasStatus}
						setOpen={setOffcanvasStatus}>
						<OffCanvasHeader
							setOpen={setOffcanvasStatus}
							style={{ paddingTop: '30px' }}>
							<OffCanvasTitle
								id='offcanvasExampleLabel'
								style={{
									fontSize: '20px',
									padding: 0,
									margin: 0,
									fontWeight: 500,
								}}>
								{t('Notifications')}
							</OffCanvasTitle>
							{notiList.length > 0 && (
								<Button
									color='danger'
									icon='Delete'
									style={{ marginLeft: '100px' }}
									onClick={() => delAllNotification()}>
									Clear All
								</Button>
							)}
						</OffCanvasHeader>
					<OffCanvasBody style={{ paddingTop: '0px', paddingRight: '10px' }} onScroll={handleScroll}>
						<div className='notifications-body'>
							{notiList && notiList.length > 0 ? (
								<>
									{notiList.map((item, index) => {
										let pathname = '';
										switch (item.notify_type) {
											case 'add_appointment':
												pathname = '/appointment/pending';
												break;
											case 'cancel_order':
											case 'complete_order':
											case 'add_order':
												pathname = '/orders';
												break;
											case 'complete_refund':
											case 'cancel_refund':
												pathname = '/refunds/completed';
												break;
											case 'cancel_appointment':
												pathname = '/appointment/cancelled';
												break;
											case 'complete_appointment':
												pathname = '/appointment/completed';
												break;
											case 'reminder':
												pathname = '/appointment/pending';
												break;
											default:
												pathname = '/';
										}

										return (
											<Alert
												key={`${item.id}-${index}`}
												icon='NotificationsNone'
												isLight
												color='info'
												className='flex-nowrap'
												isDismissible
												onClose={() => delNotification(item.id)}>
												<div
													style={{
														display: 'flex',
														flexDirection: 'column',
														cursor: 'pointer',
													}}
													onClick={() => {
														if (
															item.notify_type ===
																'add_appointment' ||
															item.notify_type ===
																'cancel_appointment' ||
															item.notify_type ===
																'complete_appointment' ||
															item.notify_type === 'reminder'
														) {
															navigate(
																`${pathname}?appid=${item.booking_code}&notify_id=${item.id}`,
															);
														} else if (
															item.notify_type === 'cancel_order' ||
															item.notify_type === 'complete_order' ||
															item.notify_type === 'add_order'
														) {
															navigate(
																`${pathname}?appid=${item.order_code}&notify_id=${item.id}`,
															);
														} else if (
															item.notify_type ===
																'complete_refund' ||
															item.notify_type === 'cancel_refund'
														) {
															navigate(
																`${pathname}?appid=${item.order_code}&notify_id=${item.id}`,
															);
														}
														// navigate(
														// 	`${pathname}?appid=${item.appointment_code}`,
														// );
														setOffcanvasStatus(false);
														setPendingParams(item.appointment_code);
													}}>
													<strong>{item.notify_title}</strong>
													<p>{item.notify_full_desc}</p>
													<small
														style={{
															textAlign: 'left',
														}}>
														{item.created_at}
													</small>
												</div>
											</Alert>
										);
										})}
										{isLoadingMore && (
											<div style={{ textAlign: 'center', padding: '20px' }}>
												<span>Loading more notifications...</span>
											</div>
										)}
									</>
								) : (
									<p style={{ marginTop: '120px', marginLeft: '135px' }}>
										No notifications
									</p>
								)}
							</div>
							{/* <Alert icon='ViewInAr' isLight color='info' className='flex-nowrap'>
						4 new components added.
					</Alert>
					<Alert icon='ThumbUp' isLight color='warning' className='flex-nowrap'>
						New products added to stock.
					</Alert>
					<Alert icon='Inventory2' isLight color='danger' className='flex-nowrap'>
						There are products that need to be packaged.
					</Alert>
					<Alert icon='BakeryDining' isLight color='success' className='flex-nowrap'>
						Your food order is waiting for you at the consultation.
					</Alert>
					<Alert icon='Escalator' isLight color='primary' className='flex-nowrap'>
						Escalator will turn off at 6:00 pm.
					</Alert> */}
						</OffCanvasBody>
					</OffCanvas>
				)}
			</HeaderRight>
		</>
	);
};
CommonHeaderRight.propTypes = {
	beforeChildren: PropTypes.node,
	afterChildren: PropTypes.node,
};
CommonHeaderRight.defaultProps = {
	beforeChildren: null,
	afterChildren: null,
};

export default CommonHeaderRight;
